import React, { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { tasksState } from '../jotai/atoms';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import './StatisticsPage.css'; // StatisticsPage.css 임포트
import axiosInstance from '../api/axiosInstance';

import LocationDistributionContent from './LocationDistributionContent'; // 지리적 분포 컴포넌트 임포트

const DashboardContent = () => {
  const currentYear = new Date().getFullYear();
  const years = ['all', ...Array.from({ length: 18 }, (_, i) => (currentYear - 13 + i).toString())];
  const [selectedYear, setSelectedYear] = useState(currentYear.toString()); // Default to current year

  const [stats, setStats] = useState({
    totalContracts: 0,
    unitPriceContracts: 0,
    lumpSumContracts: 0,
    totalContractAmount: 0,
    unitPriceTotalAmount: 0,
    lumpSumTotalAmount: 0,
    averageContractAmount: 0,
    highestContractAmount: 0,
    lowestContractAmount: 0,
    unitPriceAverageContractAmount: 0,
    unitPriceHighestContractAmount: 0,
    unitPriceLowestContractAmount: 0,
    lumpSumAverageContractAmount: 0,
    lumpSumHighestContractAmount: 0,
    lumpSumLowestContractAmount: 0,
    unitPriceCompletionTotal: 0,
    unitPriceRemainingTotal: 0,
    lumpSumCompletionTotal: 0,
    lumpSumRemainingTotal: 0,
    quarterlyData: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        let url = '/tasks/dashboard-stats';
        if (selectedYear !== 'all') {
          url += `?year=${selectedYear}`;
        }
        
        // console.log("Fetching dashboard stats from URL:", url); // Log the URL
        const response = await axiosInstance.get(url); // 백엔드 API 엔드포인트
        const data = response.data;
        // console.log("Dashboard Stats Data:", data);
        setStats(data);
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
        setError(error); // Set error state
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [selectedYear]); // Re-fetch when selectedYear changes

  if (loading) {
    return <div className="tab-content">로딩 중...</div>;
  }

  if (error) {
    return <div className="tab-content">오류: {error.message}</div>;
  }

  // Render content only if stats is available
  if (!stats) {
    return <div className="tab-content">데이터를 불러오는 중...</div>;
  }

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <br/><br/>
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'red' }}>* 총가의 금액 및 건수는 선택한 연도까지의 전체 데이터를 불러옵니다.</p>
        <div className="year-select-container" style={{ position: 'absolute', right: 0 }}>
          <select onChange={(e) => setSelectedYear(e.target.value)} value={selectedYear} className="year-select">
            <option value="all">전체</option>
            {years.filter(year => year !== 'all').map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
        </div>
      </div>

      <div className="dashboard-grid-container">
        <div className="dashboard-section">
          <h4>계약 건수 현황</h4>
          <table className="statistics-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>진행중</th>
                <th>준공</th>
                <th>총 건수</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>단가 계약</td>
                <td className="number">{stats.unitPriceInProgress?.toLocaleString() || '0'} 건</td>
                <td className="number">{stats.unitPriceCompleted?.toLocaleString() || '0'} 건</td>
                <td className="number">{(stats.unitPriceInProgress + stats.unitPriceCompleted)?.toLocaleString() || '0'} 건</td>
              </tr>
              <tr>
                <td>총가 계약</td>
                <td className="number">{stats.lumpSumInProgress?.toLocaleString() || '0'} 건</td>
                <td className="number">{stats.lumpSumCompleted?.toLocaleString() || '0'} 건</td>
                <td className="number">{(stats.lumpSumInProgress + stats.lumpSumCompleted)?.toLocaleString() || '0'} 건</td>
              </tr>
              <tr>
                <td>전체 계약</td>
                <td className="number">{(stats.unitPriceInProgress + stats.lumpSumInProgress)?.toLocaleString() || '0'} 건</td>
                <td className="number">{(stats.unitPriceCompleted + stats.lumpSumCompleted)?.toLocaleString() || '0'} 건</td>
                <td className="number">{(stats.unitPriceInProgress + stats.unitPriceCompleted + stats.lumpSumInProgress + stats.lumpSumCompleted)?.toLocaleString() || '0'} 건</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="dashboard-section">
          <h4>총 계약 금액 현황</h4>
          <table className="statistics-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>총 계약 금액</th>
                <th>준공 금액(기성)</th>
                <th>잔액</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>단가 계약</td>
                <td className="number">{stats.unitPriceTotalAmount?.toLocaleString()}원</td>
                <td className="number">{stats.unitPriceCompletionTotal?.toLocaleString()}원</td>
                <td className="number">{stats.unitPriceRemainingTotal?.toLocaleString()}원</td>
              </tr>
              <tr>
                <td>총가 계약</td>
                <td className="number">{stats.lumpSumTotalAmount?.toLocaleString()}원</td>
                <td className="number">{stats.lumpSumCompletionTotal?.toLocaleString()}원</td>
                <td className="number">{stats.lumpSumRemainingTotal?.toLocaleString()}원</td>
              </tr>
              <tr>
                <td>전체 계약</td>
                <td className="number">{(stats.unitPriceTotalAmount + stats.lumpSumTotalAmount)?.toLocaleString()}원</td>
                <td className="number">{(stats.unitPriceCompletionTotal + stats.lumpSumCompletionTotal)?.toLocaleString()}원</td>
                <td className="number">{(stats.unitPriceRemainingTotal + stats.lumpSumRemainingTotal)?.toLocaleString()}원</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="dashboard-section">
          <h4>단가 계약 금액 (최고/최저/평균)</h4>
          <table className="statistics-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>금액</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>최고 계약 금액</td>
                <td className="number">{stats.unitPriceHighestContractAmount?.toLocaleString()}원</td>
              </tr>
              <tr>
                <td>최저 계약 금액</td>
                <td className="number">{stats.unitPriceLowestContractAmount?.toLocaleString()}원</td>
              </tr>
              <tr>
                <td>평균 계약 금액</td>
                <td className="number">{stats.unitPriceAverageContractAmount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}원</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="dashboard-section">
          <h4>총가 계약 금액 (최고/최저/평균)</h4>
          <table className="statistics-table">
            <thead>
              <tr>
                <th>구분</th>
                <th>금액</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>최고 계약 금액</td>
                <td className="number">{stats.lumpSumHighestContractAmount?.toLocaleString()}원</td>
              </tr>
              <tr>
                <td>최저 계약 금액</td>
                <td className="number">{stats.lumpSumLowestContractAmount?.toLocaleString()}원</td>
              </tr>
              <tr>
                <td>평균 계약 금액</td>
                <td className="number">{stats.lumpSumAverageContractAmount?.toLocaleString(undefined, { maximumFractionDigits: 0 })}원</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AnalysisContent = () => {
  const currentYear = new Date().getFullYear();
  const [stats, setStats] = useState({
    unitPriceQuarterlyData: [],
    lumpSumYearlyData: [],
    unitPriceYearlyData: [],
    lumpSumQuarterlyData: [], // This will now be populated by unitPriceQuarterly type
  });
  const [monthlyStats, setMonthlyStats] = useState({ monthlyData: [] });
  const [quarterlyChartTab, setQuarterlyChartTab] = useState('unitPrice'); // Default to combined quarterly
  const [unitPriceQuarterlyYear, setUnitPriceQuarterlyYear] = useState(currentYear.toString()); // This will control the year for combined quarterly
  const [lumpSumQuarterlyStartYear, setLumpSumQuarterlyStartYear] = useState((currentYear - 10).toString());
  const [lumpSumQuarterlyEndYear, setLumpSumQuarterlyEndYear] = useState(currentYear.toString());
  const [unitPriceYearlyStartYear, setUnitPriceYearlyStartYear] = useState((currentYear - 10).toString());
  const [unitPriceYearlyEndYear, setUnitPriceYearlyEndYear] = useState(currentYear.toString());
  const [monthlyChartYear, setMonthlyChartYear] = useState(currentYear.toString());
  const [monthlyChartTypeTab, setMonthlyChartTypeTab] = useState('all'); // 'all', 'unitPrice', 'lumpSum'
  // Removed lumpSumQuarterlyYear state as it's now combined
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalysisStats = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `/tasks/analysis-stats?`;
        const params = [];

        if (quarterlyChartTab === 'unitPrice') { // This tab now handles both unitPrice and lumpSum quarterly
          params.push(`type=unitPriceQuarterly`); // Backend will return both unitPriceQuarterlyData and lumpSumQuarterlyData
          params.push(`year=${unitPriceQuarterlyYear}`);
        } else if (quarterlyChartTab === 'lumpSum') {
          params.push(`type=lumpSumYearly`);
          params.push(`startYear=${lumpSumQuarterlyStartYear}`);
          params.push(`endYear=${lumpSumQuarterlyEndYear}`);
        } else if (quarterlyChartTab === 'unitPriceYearly') {
          params.push(`type=unitPriceYearly`);
          params.push(`startYear=${unitPriceYearlyStartYear}`);
          params.push(`endYear=${unitPriceYearlyEndYear}`);
        }
        
        url += params.join('&');
        const response = await axiosInstance.get(url);
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch analysis stats:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchMonthlyStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(`/tasks/monthly-stats?year=${monthlyChartYear}`);
        console.log("Raw Monthly Stats Data from Backend:", response.data);
        setMonthlyStats(response.data);
        console.log("Processed Monthly Stats for Chart:", response.data.monthlyData.map(item => ({
          name: `${item.month}월`,
          단가계약건수: item.unitPriceContractCount,
          총가계약건수: item.lumpSumContractCount,
          단가준공건수: item.unitPriceCompletionCount,
          총가준공건수: item.lumpSumCompletionCount,
          단가계약공사명: item.unitPriceContractTaskNames,
          총가계약공사명: item.lumpSumContractTaskNames,
          단가준공공사명: item.unitPriceCompletionTaskNames,
          총가준공공사명: item.lumpSumCompletionTaskNames,
        })));
      } catch (error) {
        console.error("Failed to fetch monthly stats:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisStats();
    fetchMonthlyStats();
  }, [quarterlyChartTab, unitPriceQuarterlyYear, lumpSumQuarterlyStartYear, lumpSumQuarterlyEndYear, unitPriceYearlyStartYear, unitPriceYearlyEndYear, monthlyChartYear]); // Removed lumpSumQuarterlyYear dependency

  if (loading) {
    return <div className="tab-content">로딩 중...</div>;
  }

  if (error) {
    return <div className="tab-content">오류: {error.message}</div>;
  }

//   console.log("Analysis Stats:", stats); // Keep console.log

//   console.log("Mapped data:", (stats.unitPriceQuarterlyData || []).map(item => ({ 
//   name: item.quarter.replace('Q', '') + '분기', 
//   단가금액: item.amount, 
//   단가건수: item.count, 
//   총가금액: (stats.lumpSumQuarterlyData || []).find(lsItem => lsItem.quarter === item.quarter)?.amount, 
//   총가건수: (stats.lumpSumQuarterlyData || []).find(lsItem => lsItem.quarter === item.quarter)?.count 
// })));

  const yearOptions = Array.from({ length: 21 }, (_, i) => (currentYear - 15 + i).toString());

  return (
    <div className="tab-content">
      <h4>분기별/연도별 계약 현황</h4>
      <div className="tabs">
        <button
          className={`tab-button ${quarterlyChartTab === 'unitPrice' ? 'active' : ''}`}
          onClick={() => setQuarterlyChartTab('unitPrice')}
        >
          분기별 (단가/총가)
        </button>
        <button
          className={`tab-button ${quarterlyChartTab === 'unitPriceYearly' ? 'active' : ''}`}
          onClick={() => setQuarterlyChartTab('unitPriceYearly')}
        >
          단가 (연도별)
        </button>
        <button
          className={`tab-button ${quarterlyChartTab === 'lumpSum' ? 'active' : ''}`}
          onClick={() => setQuarterlyChartTab('lumpSum')}
        >
          총가 (연도별)
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
        {quarterlyChartTab === 'unitPrice' && (
          <select onChange={(e) => setUnitPriceQuarterlyYear(e.target.value)} value={unitPriceQuarterlyYear} className="year-select">
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
        )}
        {quarterlyChartTab === 'lumpSum' && (
          <>
            <select onChange={(e) => setLumpSumQuarterlyStartYear(e.target.value)} value={lumpSumQuarterlyStartYear} className="year-select">
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
            <span style={{ margin: '0 5px' }}>~</span>
            <select onChange={(e) => setLumpSumQuarterlyEndYear(e.target.value)} value={lumpSumQuarterlyEndYear} className="year-select">
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
          </>
        )}
        {quarterlyChartTab === 'unitPriceYearly' && (
          <>
            <select onChange={(e) => setUnitPriceYearlyStartYear(e.target.value)} value={unitPriceYearlyStartYear} className="year-select">
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
            <span style={{ margin: '0 5px' }}>~</span>
            <select onChange={(e) => setUnitPriceYearlyEndYear(e.target.value)} value={unitPriceYearlyEndYear} className="year-select">
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}년</option>
              ))}
            </select>
          </>
        )}
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={
            quarterlyChartTab === 'unitPrice' ? (stats.unitPriceQuarterlyData || []).map(item => ({ name: item.quarter.replace('Q', '') + '분기', 단가금액: item.amount, 단가건수: item.count, 총가금액: (stats.lumpSumQuarterlyData || []).find(lsItem => lsItem.quarter === item.quarter)?.amount, 총가건수: (stats.lumpSumQuarterlyData || []).find(lsItem => lsItem.quarter === item.quarter)?.count })) : // Unit Price Quarterly
            quarterlyChartTab === 'lumpSum' ? (stats.lumpSumYearlyData || []).map(item => ({ name: item.year + '년', 금액: item.amount })) :
            (stats.unitPriceYearlyData || []).map(item => ({ name: item.year + '년', 금액: item.amount }))
          }>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" formatter={(value) => `${value.toLocaleString()}원`} /> {/* Left YAxis for Amount */}
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length > 0) {
                  const data = payload[0].payload; // 전체 데이터 객체
                  return (
                    <div className="custom-tooltip" style={{
                      backgroundColor: 'white',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
                      {quarterlyChartTab === 'unitPrice' && (
                        <>
                          <p style={{ margin: '2px 0', color: '#8884d8' }}>
                            단가 계약 금액: {data.단가금액?.toLocaleString()}원
                          </p>
                          <p style={{ margin: '2px 0', color: '#8884d8' }}>
                            단가 계약 건수: {data.단가건수?.toLocaleString()}건
                          </p>
                          <p style={{ margin: '2px 0', color: '#ffc658' }}>
                            총가 계약 금액: {data.총가금액?.toLocaleString()}원
                          </p>
                          <p style={{ margin: '2px 0', color: '#ffc658' }}>
                            총가 계약 건수: {data.총가건수?.toLocaleString()}건
                          </p>
                        </>
                      )}
                      {quarterlyChartTab === 'lumpSum' && (
                        <p style={{ margin: '2px 0', color: '#82ca9d' }}>
                          계약 금액: {data.금액?.toLocaleString()}원
                        </p>
                      )}
                      {quarterlyChartTab === 'unitPriceYearly' && (
                        <p style={{ margin: '2px 0', color: '#ffc658' }}>
                          계약 금액: {data.금액?.toLocaleString()}원
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            {quarterlyChartTab === 'unitPrice' && ( // Combined quarterly graph
              <>
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="단가금액"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                  dot={true}
                  name="단가 계약 금액"
                />
                <Line
                  yAxisId="left" // Changed to left YAxis for consistency, as count line is hidden
                  type="monotone"
                  dataKey="총가금액"
                  stroke="#ffc658"
                  activeDot={{ r: 8 }}
                  dot={true}
                  name="총가 계약 금액"
                />
              </>
            )}
            {quarterlyChartTab === 'lumpSum' && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="금액"
                stroke="#82ca9d"
                activeDot={{ r: 8 }}
                dot={true}
                name="계약 금액"
              />
            )}
            {quarterlyChartTab === 'unitPriceYearly' && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="금액"
                stroke="#ffc658"
                activeDot={{ r: 8 }}
                dot={true}
                name="계약 금액"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <hr style={{ margin: '20px 0', borderColor: '#eee' }}/>
      <br/>
      <hr style={{ margin: '20px 0', borderColor: '#eee' }}/>

      <h4>월별 계약/준공 현황</h4>
      <div className="tabs">
        <button
          className={`tab-button ${monthlyChartTypeTab === 'all' ? 'active' : ''}`}
          onClick={() => setMonthlyChartTypeTab('all')}
        >
          전체
        </button>
        <button
          className={`tab-button ${monthlyChartTypeTab === 'unitPrice' ? 'active' : ''}`}
          onClick={() => setMonthlyChartTypeTab('unitPrice')}
        >
          단가
        </button>
        <button
          className={`tab-button ${monthlyChartTypeTab === 'lumpSum' ? 'active' : ''}`}
          onClick={() => setMonthlyChartTypeTab('lumpSum')}
        >
          총가
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
        <select onChange={(e) => setMonthlyChartYear(e.target.value)} value={monthlyChartYear} className="year-select">
          {yearOptions.map(year => (
            <option key={year} value={year}>{year}년</option>
          ))}
        </select>
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={monthlyStats.monthlyData.map(item => {
            const baseData = {
              name: `${item.month}월`,
              단가계약건수: item.unitPriceContractCount,
              총가계약건수: item.lumpSumContractCount,
              단가준공건수: item.unitPriceCompletionCount,
              총가준공건수: item.lumpSumCompletionCount,
              단가계약공사명: item.unitPriceContractTaskNames,
              총가계약공사명: item.lumpSumContractTaskNames,
              단가준공공사명: item.unitPriceCompletionTaskNames,
              총가준공공사명: item.lumpSumCompletionTaskNames,
            };

            if (monthlyChartTypeTab === 'unitPrice') {
              return {
                name: baseData.name,
                계약건수: baseData.단가계약건수,
                준공건수: baseData.단가준공건수,
                계약공사명: baseData.단가계약공사명,
                준공공사명: baseData.단가준공공사명,
              };
            } else if (monthlyChartTypeTab === 'lumpSum') {
              return {
                name: baseData.name,
                계약건수: baseData.총가계약건수,
                준공건수: baseData.총가준공건수,
                계약공사명: baseData.총가계약공사명,
                준공공사명: baseData.총가준공공사명,
              };
            } else { // 'all' tab
              return {
                name: baseData.name,
                단가계약건수: baseData.단가계약건수,
                총가계약건수: baseData.총가계약건수,
                단가준공건수: baseData.단가준공건수,
                총가준공건수: baseData.총가준공건수,
                단가계약공사명: baseData.단가계약공사명,
                총가계약공사명: baseData.총가계약공사명,
                단가준공공사명: baseData.단가준공공사명,
                총가준공공사명: baseData.총가준공공사명,
              };
            }
          })}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip formatter={(value, name, props) => {
              const data = props.payload;
              if (name === '단가계약건수') return [`${value}건`, `단가 계약 (${data.단가계약공사명.join(', ')})`];
              if (name === '총가계약건수') return [`${value}건`, `총가 계약 (${data.총가계약공사명.join(', ')})`];
              if (name === '단가준공건수') return [`${value}건`, `단가 준공 (${data.단가준공공사명.join(', ')})`];
              if (name === '총가준공건수') return [`${value}건`, `총가 준공 (${data.총가준공공사명.join(', ')})`];
              if (name === '계약건수') return [`${value}건`, `계약 (${data.계약공사명.join(', ')})`];
              if (name === '준공건수') return [`${value}건`, `준공 (${data.준공공사명.join(', ')})`];
              return [value, name];
            }} />
            <Legend />
            {monthlyChartTypeTab === 'all' ? (
              <>
                <Bar dataKey="단가계약건수" fill="#8884d8" name="단가 계약" />
                <Bar dataKey="총가계약건수" fill="#FFD700" name="총가 계약" />
                <Bar dataKey="단가준공건수" fill="#82ca9d"name="단가 준공" />
                <Bar dataKey="총가준공건수" fill="#DAA520" name="총가 준공" />
              </>
            ) : monthlyChartTypeTab === 'unitPrice' ? (
              <>
                <Bar dataKey="계약건수" fill="#8884d8"name="단가 계약" />
                <Bar dataKey="준공건수" fill="#82ca9d" name="단가 준공" />
              </>
            ) : (
              <>
                <Bar dataKey="계약건수" fill="#FFD700" name="총가 계약" />
                <Bar dataKey="준공건수" fill="#DAA520" name="총가 준공" />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const AmountStatusContent = () => {
  const currentYear = new Date().getFullYear();
  const years = ['all', ...Array.from({ length: 11 }, (_, i) => (currentYear - 5 + i).toString())];

  const [amountStats, setAmountStats] = useState({
    unitPriceTotal: 0,
    unitPriceCompletionTotal: 0,
    unitPriceRemainingTotal: 0,
    lumpSumTotal: 0,
    lumpSumCompletionTotal: 0,
    lumpSumRemainingTotal: 0,
    totalContractAmount: 0,
    totalCompletionAmount: 0,
    totalRemainingAmount: 0,
  });
  const [contractAmountTab, setContractAmountTab] = useState('unitPrice');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API 호출 함수를 별도로 분리
  const fetchAmountStats = async (year, tab) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/tasks/amount-stats?year=${year}&type=${tab}`);
      // console.log("Amount Stats Data:", response.data);
      setAmountStats(response.data);
    } catch (error) {
      console.error("Failed to fetch amount stats:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  // 탭 변경시 기본 연도 설정 및 즉시 API 호출
  const handleTabChange = (newTab) => {
    let newYear;
    if (newTab === 'unitPrice') {
      newYear = currentYear.toString();
    } else if (newTab === 'lumpSum') {
      newYear = 'all';
    } else {
      newYear = selectedYear; // 'all' 탭의 경우 현재 선택된 연도 유지
    }
    
    setContractAmountTab(newTab);
    setSelectedYear(newYear);
    
    // 즉시 API 호출
    fetchAmountStats(newYear, newTab);
  };

  // selectedYear가 수동으로 변경될 때만 API 호출
  useEffect(() => {
    fetchAmountStats(selectedYear, contractAmountTab);
  }, [selectedYear, contractAmountTab]); // 필요한 의존성 모두 포함

  // 컴포넌트 마운트 시 초기 데이터 로드는 위의 useEffect가 처리하므로 제거

  if (loading) {
    return <div className="tab-content">로딩 중...</div>;
  }

  if (error) {
    return <div className="tab-content">오류: {error.message}</div>;
  }

  // Recharts Data with percentage calculation
  const getTotalAmount = () => {
    return contractAmountTab === 'unitPrice' ? amountStats.unitPriceTotal : 
           (contractAmountTab === 'lumpSum' ? amountStats.lumpSumTotal : amountStats.totalContractAmount);
  };

  const getCompletionAmount = () => {
    return contractAmountTab === 'unitPrice' ? amountStats.unitPriceCompletionTotal : 
           (contractAmountTab === 'lumpSum' ? amountStats.lumpSumCompletionTotal : amountStats.totalCompletionAmount);
  };

  const getRemainingAmount = () => {
    return contractAmountTab === 'unitPrice' ? amountStats.unitPriceRemainingTotal : 
           (contractAmountTab === 'lumpSum' ? amountStats.lumpSumRemainingTotal : amountStats.totalRemainingAmount);
  };

  const totalAmount = getTotalAmount();
  const completionAmount = getCompletionAmount();
  const remainingAmount = getRemainingAmount();
  
  const completionPercentage = totalAmount > 0 ? ((completionAmount / totalAmount) * 100).toFixed(1) : 0;
  const remainingPercentage = totalAmount > 0 ? ((remainingAmount / totalAmount) * 100).toFixed(1) : 0;

  const chartData = [
    {
      name: '총 계약 금액',
      value: totalAmount,
      percentage: 100,
      label: '총 계약 금액 (100%)'
    },
    {
      name: '준공 금액',
      value: completionAmount,
      percentage: completionPercentage,
      label: `준공 금액 (${completionPercentage}%)`
    },
    {
      name: '잔액',
      value: remainingAmount,
      percentage: remainingPercentage,
      label: `잔액 (${remainingPercentage}%)`
    },
  ];

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <br/><br/>
        <h2 style={{ margin: 0 }}>계약 금액 현황</h2>
        <div className="year-select-container">
          <select onChange={(e) => setSelectedYear(e.target.value)} value={selectedYear} className="year-select">
            <option value="all">전체</option>
            {years.filter(year => year !== 'all').map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
        </div>
      </div>
      <div className="tabs">
        <button
          className={`tab-button ${contractAmountTab === 'unitPrice' ? 'active' : ''}`}
          onClick={() => handleTabChange('unitPrice')}
        >
          단가
        </button>
        <button
          className={`tab-button ${contractAmountTab === 'lumpSum' ? 'active' : ''}`}
          onClick={() => handleTabChange('lumpSum')}
        >
          총가
        </button>
        <button
          className={`tab-button ${contractAmountTab === 'all' ? 'active' : ''}`}
          onClick={() => handleTabChange('all')}
        >
          전체
        </button>
      </div>
      <div className="tab-content">
        <table className="statistics-table">
          <thead>
            <tr>
              <th>구분</th>
              <th>총 계약 금액(공급액)</th>
              <th>준공 금액(기성)</th>
              <th>잔액</th>
            </tr>
          </thead>
          <tbody>
            {/* 단가 행 */}
            {contractAmountTab === 'unitPrice' || contractAmountTab === 'all' ? (
              <tr>
                <td>단가</td>
                <td className="number">{amountStats.unitPriceTotal.toLocaleString()}원</td>
                <td className="number">{amountStats.unitPriceCompletionTotal.toLocaleString()}원</td>
                <td className="number">{amountStats.unitPriceRemainingTotal.toLocaleString()}원</td>
              </tr>
            ) : null}

            {/* 총가 행 */}
            {contractAmountTab === 'lumpSum' || contractAmountTab === 'all' ? (
              <tr>
                <td>총가</td>
                <td className="number">{amountStats.lumpSumTotal.toLocaleString()}원</td>
                <td className="number">{amountStats.lumpSumCompletionTotal.toLocaleString()}원</td>
                <td className="number">{amountStats.lumpSumRemainingTotal.toLocaleString()}원</td>
              </tr>
            ) : null}

            {/* 전체 행 (all 탭에서만 보이도록) */}
            {contractAmountTab === 'all' ? (
              <tr className="total-row">
                <td>전체</td>
                <td className="number">{amountStats.totalContractAmount.toLocaleString()}원</td>
                <td className="number">{amountStats.totalCompletionAmount.toLocaleString()}원</td>
                <td className="number">{amountStats.totalRemainingAmount.toLocaleString()}원</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <br/><hr/><br/>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <XAxis dataKey="label" />
            <YAxis formatter={(value) => `${value.toLocaleString()}원`} />
            <Tooltip 
              formatter={(value, name, props) => [
                `${value.toLocaleString()}원`,
                props.payload.label
              ]} 
            />
            <Legend />
            <Bar dataKey="value" fill="#3ea200ff">
              <LabelList 
                dataKey="percentage" 
                position="top" 
                formatter={(value) => `${value}%`}
                style={{ fontSize: '12px', fontWeight: 'bold' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const StatisticsPage = () => {
  const tasks = useAtomValue(tasksState);
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'amountStatus', 'analysis', 'location'

  return (
    <div className="container">
      <div className="quadrant bottom-left" style={{ width: '100%', margin: '0 auto' }}>
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            공사 현황
          </button>
          <button
            className={`tab-button ${activeTab === 'amountStatus' ? 'active' : ''}`}
            onClick={() => setActiveTab('amountStatus')}
          >
            금액 현황
          </button>
          <button
            className={`tab-button ${activeTab === 'analysis' ? 'active' : ''}`}
            onClick={() => setActiveTab('analysis')}
          >
            분석
          </button>
          <button
            className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
            onClick={() => setActiveTab('location')}
          >
            공사지역분포
          </button>
        </div>
        {activeTab === 'dashboard' && <DashboardContent tasks={tasks} />}
        {activeTab === 'amountStatus' && <AmountStatusContent />}
        {activeTab === 'analysis' && <AnalysisContent />}
        {activeTab === 'location' && <LocationDistributionContent />}
      </div>
    </div>
  );
};

export default StatisticsPage;